import cx from "classnames";
import { Box } from "components/Box";
import { Caret } from "components/Caret";
import { Clamp } from "components/Clamp";
import { Collapse } from "components/Collapse";
import { Drawer } from "components/Drawer/Drawer";
import { EventStatus } from "components/layout/EventStatus";
import { PromoIcon } from "components/PromoIcon";
import isPast from "date-fns/isPast";
import { useIsLoggedIn, useIsMobile } from "hooks";
import type { ToggleSelectionPayload } from "hooks/firestore/betting/types";
import type {
  Event,
  EventMarket,
  Outcome,
} from "hooks/firestore/betting/useBetting";
import { ErrorBoundary } from "library";
import { groupBy } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { sortMarkets } from "utilities/sortMarkets";
import { CompetitorAvatar } from "../components/CompetitorAvatar";
import OutcomesGroup from "./OutcomesGroup";

import { ReactComponent as LockIcon } from "./assets/lock.svg";
import { ReactComponent as TickIcon } from "./assets/tick.svg";

import * as styles from "./MarketGroup.module.scss";

const forcedFullWidthMarkets = [
  "Away first quarter points over/under",
  "Home first quarter points over/under",
  "Total first quarter points exact",
  "Away match points over/under",
  "Home match points over/under",
  "Both teams match points over/under",
  "Total match points exact",
  "Away first half points over/under",
  "Home first half points over/under",
  "Away match points over/under",
  "Home match points over/under",
  "Away total runs over/under",
  "Home total runs over/under",
  "Away home-runs after innings 1 over/under",
  "Home home-runs after innings 1 over/under",
  "Away home-runs after innings 2 over/under",
  "Home home-runs after innings 2 over/under",
  "Away home-runs after innings 3 over/under",
  "Home home-runs after innings 3 over/under",
  "Away home-runs after innings 4 over/under",
  "Home home-runs after innings 4 over/under",
  "Away home-runs after innings 5 over/under",
  "Home home-runs after innings 5 over/under",
  "Away total runs after innings 1 over/under",
  "Home total runs after innings 1 over/under",
  "Away total runs after innings 2 over/under",
  "Home total runs after innings 2 over/under",
  "Away total runs after innings 3 over/under",
  "Home total runs after innings 3 over/under",
  "Away total runs after innings 4 over/under",
  "Home total runs after innings 4 over/under",
  "Away total runs after innings 5 over/under",
  "Home total runs after innings 5 over/under",
  "Away home-runs in innings 1 over/under",
  "Home home-runs in innings 1 over/under",
  "Away home-runs in innings 2 over/under",
  "Home home-runs in innings 2 over/under",
  "Away home-runs in innings 3 over/under",
  "Home home-runs in innings 3 over/under",
  "Away home-runs in innings 4 over/under",
  "Home home-runs in innings 4 over/under",
  "Away home-runs in innings 5 over/under",
  "Home home-runs in innings 5 over/under",
  "Away total runs in innings 1 over/under",
  "Home total runs in innings 1 over/under",
  "Away total runs in innings 2 over/under",
  "Home total runs in innings 2 over/under",
  "Away total runs in innings 3 over/under",
  "Home total runs in innings 3 over/under",
  "Away total runs in innings 4 over/under",
  "Home total runs in innings 4 over/under",
  "Away total runs in innings 5 over/under",
  "Home total runs in innings 5 over/under",
];

const excludedFullWidthMarkets = [
  /Match tries exactly/,
  /Match points exactly/,
  /First half points exactly/,
  /First half tries exactly/,
  /Second half points exactly/,
  /Second half tries exactly/,
  /Total first quarter points exact/,
  /Total match points exact/,
  /Win both halves/,
  /Match winner/,
  /1x2/,
  /Draw no bet/,
  /1st half - 1x2/,
  /Winner 1st half/,
  /Match winner three way/,
  /Winner/,
  /First quarter winner/,
  /Winner \(incl\. extra innings\)/,
  /Odd\/even \(incl\. extra innings\)/,
  /1st inning - 1x2/,
  /1st inning - total/,
  /1st inning - Winner/,
  /Innings 1 to 5 - Winner/,
  /Will there be an extra inning/,
  /Extra innings played/,
  /.*total points.*/,
  /.*odd\/even.*/,
  /.*over\/under.*/,
  /.*under\/over.*/,
  /Total/,
  /Odd\/even \(incl. overtime\)/,
  /.+ - draw no bet/,
  /.+ - total/,
  /.+ total points \(incl. overtime\)/,
  /.+ total assists \(incl. overtime\)/,
  /.+ total rebounds \(incl. overtime\)/,
  /.+ total 3-point field goals \(incl. overtime\)/,
  /Will there be overtime/,
  /Odd total tries/,
  /Even total tries/,
  /Home-runs in innings \d+ exactly/,
  /Home-runs after innings \d+ exactly/,
  /Match home-runs exactly/,
  /Match total runs exactly/,
];

export const isExcludedFullWidthMarket = (markets: EventMarket[]) => {
  // we check the market, and if there are only two outcomes, and the abbreviation/name are no and yes, we should show them as 2 columns.
  if (
    markets.some((market) => {
      const outcomes = Object.values(market?.outcomes || {});
      if (outcomes.length !== 2) return false;
      return outcomes?.every((outcome) => {
        const outcomeName = (
          outcome.abbreviation ? outcome?.abbreviation : outcome?.name
        )?.toLowerCase();
        return outcomeName === "yes" || outcomeName === "no";
      });
    })
  ) {
    return true;
  }

  // we have for example a regex pattern that is catching all over/under to exclude them and show them as single columns.
  // In some cases we should not exclude them though.
  if (markets.some((market) => forcedFullWidthMarkets.includes(market.name))) {
    return false;
  }

  return markets.some((market) =>
    excludedFullWidthMarkets.some((regex) => regex.test(market.name)),
  );
};

const Market = ({
  groupedMarkets,
  groupName,
  oddsFormat,
  toggleSelection,
  selections,
  event,
  isOutright,
  isLoggedIn,
  open,
}: {
  groupedMarkets: EventMarket[];
  groupName: string;
  oddsFormat: any;
  toggleSelection: (selection: ToggleSelectionPayload) => void;
  selections: string[];
  event: Event;
  isOutright: boolean;
  isLoggedIn: boolean;
  open: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(open);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (open !== isOpen) {
      setIsOpen(open);
    }
  }, [open]);

  const getCompetitorAndLogoByType = (market, types) => {
    const outcome = Object.values(market.outcomes).find((outcome: Outcome) =>
      types.includes(outcome.type),
    ) as Outcome;

    const competitor = event?.competitors[outcome?.competitorId];

    if (!competitor || !outcome) {
      return null;
    }

    return (
      <div
        className={cx(styles.franchiseLogo, {
          [styles.left]: outcome.type?.includes("HOME"),
        })}
      >
        <span className={styles.franchiseName}>{competitor.name}</span>
        <CompetitorAvatar className={styles.avatar} competitor={competitor} />
      </div>
    );
  };

  const shouldLiftTeamNames = groupedMarkets.every((market) =>
    Object.values(market.outcomes).every(
      //If all outcome abbreviations are numbers (e.g +1.5, -0.5) then user is lacking context of the related team
      //we lift the team names and display them above the list. Typically used for handicap markets.
      (outcome: Outcome) => outcome.abbreviation?.search(/^[-+]?[0-9.]*$/) == 0,
    ),
  );
  const isGroupSuspended = groupedMarkets.every(
    (market) =>
      market.status === "SUSPENDED" ||
      market.status === "DEACTIVATED" ||
      market.status === "UNAVAILABLE",
  );
  const isGroupResulted = groupedMarkets.every(
    (market) => market.status === "SETTLED" || market.status === "RESULTED",
  );

  const isFullWidth = useMemo(() => {
    return (
      //
      groupedMarkets.some((x) => x.marketType === "CUSTOM") ||
      (isMobile &&
        !isExcludedFullWidthMarket(groupedMarkets) &&
        !shouldLiftTeamNames) ||
      isOutright
    );
  }, [groupedMarkets, isMobile, isOutright, shouldLiftTeamNames]);

  const markets = useMemo(
    () => (
      <div
        className={cx(styles.markets, {
          [styles.noBorder]: isOutright,
        })}
      >
        {shouldLiftTeamNames ? (
          <div
            className={cx(styles.franchiseLogos, {
              [styles.darken]: isGroupSuspended,
            })}
          >
            {getCompetitorAndLogoByType(groupedMarkets[0], [
              "FRANCHISE_HOME",
              "PLAYER_HOME",
            ])}
            {getCompetitorAndLogoByType(groupedMarkets[0], [
              "FRANCHISE_AWAY",
              "PLAYER_AWAY",
            ])}
          </div>
        ) : null}
        <OutcomesGroup
          groupedMarkets={groupedMarkets}
          oddsFormat={oddsFormat}
          toggleSelection={toggleSelection}
          selections={selections}
          event={event}
          isOutright={isOutright}
          fullWidth={isFullWidth}
        />
      </div>
    ),
    [
      groupedMarkets,
      oddsFormat,
      toggleSelection,
      selections,
      event,
      isOutright,
      isFullWidth,
    ],
  );

  const promotionIsAvailable = useMemo(
    () =>
      groupedMarkets.some(
        (market) =>
          market.hasPromotionalMarkets || market.promotionCampaigns?.length,
      ),
    [groupedMarkets],
  );

  if (isOutright) {
    return markets;
  }

  return (
    <ErrorBoundary key={`market-group-${groupName}`}>
      <Box
        hideHeader
        noChildrenSpace={!isOpen}
        className={cx(styles.marketCard, {
          [styles.suspended]: isGroupSuspended,
        })}
      >
        <div
          className={styles.marketGroup}
          onClick={() => setIsOpen((visible) => !visible)}
        >
          <div
            className={cx(styles.name, {
              [styles.darken]: isGroupResulted || isGroupSuspended,
            })}
          >
            {groupName}
          </div>
          <div className={styles.headingRight}>
            {isLoggedIn && promotionIsAvailable && <PromoIcon />}

            {isGroupSuspended && (
              <div className={styles.lock}>
                <LockIcon />
              </div>
            )}

            {isGroupResulted && (
              <div className={styles.tick}>
                <TickIcon />
              </div>
            )}

            <Clamp variant="grey" className={styles.clamp}>
              <Caret variant={isOpen ? "up" : "down"} />
            </Clamp>
          </div>
        </div>

        <Collapse isOpen={isOpen}>{markets}</Collapse>
      </Box>
    </ErrorBoundary>
  );
};

type MarketGroupProps = {
  name: string;
  markets: EventMarket[];
  event: Event;
  oddsFormat?: any;
  isOpen?: boolean;
  toggleMarkets?: () => void;
  isOutright?: boolean;
  toggleSelection: (selection: ToggleSelectionPayload) => void;
  selections: string[];
  isSearchActive?: boolean;
  currentGroupIndex?: number;
};

export type OutcomeWithMarketType = Outcome & {
  id: string;
  market: {
    id: string;
    name: string;
    bettingType: string;
    status: string;
    category: string;
    marketType: string;
    hasPromotionalMarkets?: boolean;
  };
};

export type OutcomesWithMarketType = {
  [key: string]: OutcomeWithMarketType;
};

const OutrightCluster = ({
  outright,
  markets,
}: { outright: Event; markets: EventMarket[] }) => {
  const nextBetStop = markets?.[0].nextBetStop;
  const isOutrightSuspended =
    ["SUSPENDED", "DEACTIVATED", "CANCELLED"].includes(outright.status) ||
    (nextBetStop && isPast(nextBetStop));

  return (
    <>
      {isOutrightSuspended && (
        <div className={cx(styles.lock, styles.suspendedOutright)}>
          <LockIcon />
        </div>
      )}

      <EventStatus
        status={outright.status}
        scheduledStartTime={nextBetStop || outright.scheduledStartTime}
        shape="rounded"
        variant="dark"
      />
    </>
  );
};

const MarketGroup = ({
  name,
  markets,
  oddsFormat,
  isOpen = false,
  toggleMarkets,
  isOutright = false,
  toggleSelection,
  event,
  selections,
  isSearchActive,
  currentGroupIndex,
}: MarketGroupProps) => {
  const isLoggedIn = useIsLoggedIn();

  const sortedMarkets = useMemo(() => {
    return groupBy(
      sortMarkets(markets).filter((m) => {
        const hasOutcomes = Object.keys(m.outcomes ?? {}).length > 0;

        if (m.attributes?.custom && hasOutcomes) {
          // promotional custom markets cannot be shown to unauthenticated users
          return m.promotional || m.showPromoBanner ? isLoggedIn : true;
        }

        return hasOutcomes;
      }),
      "name",
    );
  }, [markets, isLoggedIn]);

  const categoryHasPromotionalMarkets = markets.some(
    (market) => market.showPromoBanner,
  );

  return (
    <div className={cx(styles.group, { [styles.open]: isOpen })}>
      <div className={styles.trigger} onClick={toggleMarkets}>
        <div className={styles.name}>{name}</div>

        <div className={styles.headingRightGroup}>
          {isLoggedIn && categoryHasPromotionalMarkets && <PromoIcon />}

          {isOutright && <OutrightCluster outright={event} markets={markets} />}

          <Clamp variant="dark" shape="rounded">
            {Object.keys(sortedMarkets).length}
            <Caret variant={isOpen ? "up" : "down"} />
          </Clamp>
        </div>
      </div>

      <Drawer isOpen={isOpen} className={styles.draw}>
        {Object.keys(sortedMarkets).map((groupName, i) => {
          return (
            <Market
              key={`market-${groupName}`}
              groupName={groupName}
              groupedMarkets={sortedMarkets[groupName]}
              oddsFormat={oddsFormat}
              toggleSelection={toggleSelection}
              selections={selections}
              event={event}
              isOutright={isOutright}
              isLoggedIn={isLoggedIn}
              open={
                isSearchActive ||
                (currentGroupIndex === 0 && i === 0) ||
                Object.keys(sortedMarkets).length === 1
              }
            />
          );
        })}
      </Drawer>
    </div>
  );
};

export default MarketGroup;
